import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import FloatingWhatsApp from '../../components/feature/FloatingWhatsApp';
import { getOrganizationSchema } from '@/constants/schema';

const SITE_URL = (import.meta.env.VITE_SITE_URL as string) || 'https://omandisposables.com';

const PRIVACY_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    getOrganizationSchema(),
    {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/privacy-policy#webpage`,
      name: 'Privacy Policy | omandisposables.com',
      url: `${SITE_URL}/privacy-policy`,
      description: 'Privacy Policy for omandisposables.com — how we collect, use, and protect your data.',
      inLanguage: ['en', 'ar'],
      isPartOf: { '@id': `${SITE_URL}/#organization` },
      breadcrumb: { '@id': `${SITE_URL}/privacy-policy#breadcrumb` },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${SITE_URL}/privacy-policy#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Privacy Policy', item: `${SITE_URL}/privacy-policy` },
      ],
    },
  ],
};

const SECTIONS = [
  {
    icon: 'ri-user-line',
    title: 'Information We Collect',
    content: [
      'When you fill out our contact form or Request a Quote form, we collect personal information such as your name, email address, phone number, company name, and any details you provide about your order.',
      'We may also automatically collect certain technical data when you visit our website, including your IP address, browser type, device type, pages visited, and time spent on the site. This data is collected through cookies and analytics tools.',
    ],
  },
  {
    icon: 'ri-settings-3-line',
    title: 'How We Use Your Information',
    content: [
      'To respond to your quote requests and enquiries regarding wholesale disposable gloves.',
      'To communicate with you via WhatsApp or email about your order or business needs.',
      'To improve our website experience and understand how visitors interact with our content.',
      'To comply with any applicable legal or regulatory obligations.',
    ],
  },
  {
    icon: 'ri-bar-chart-box-line',
    title: 'Cookies & Analytics',
    content: [
      'We use cookies to help our website function correctly and to gather anonymous analytics data. These analytics cookies allow us to understand how visitors use our site so we can improve it.',
      'You can control or disable cookies through your browser settings. Disabling cookies may affect some functionality of the website.',
      'We do not use cookies to serve personalised advertising or track you across third-party websites.',
    ],
  },
  {
    icon: 'ri-share-line',
    title: 'Data Sharing',
    content: [
      'We do not sell, rent, or trade your personal data to any third parties.',
      'We may share your data with trusted service providers who assist us in operating our website (e.g., hosting and analytics providers), but only to the extent necessary and under strict confidentiality agreements.',
      'We may disclose information if required by law or to protect the rights and safety of our business and customers.',
    ],
  },
  {
    icon: 'ri-lock-line',
    title: 'Data Security',
    content: [
      'We take reasonable precautions to protect your personal information from unauthorised access, misuse, or disclosure.',
      'All communication on our website is secured via HTTPS. However, no method of internet transmission is 100% secure, and we cannot guarantee absolute security.',
    ],
  },
  {
    icon: 'ri-timer-line',
    title: 'Data Retention',
    content: [
      'We retain your contact information only for as long as necessary to respond to your enquiry and for a reasonable period thereafter for record-keeping purposes.',
      'You may request deletion of your data at any time by contacting us at the email address below.',
    ],
  },
  {
    icon: 'ri-link',
    title: 'Third-Party Links',
    content: [
      'Our website may contain links to external websites such as WhatsApp. We are not responsible for the privacy practices of those sites. We encourage you to review their privacy policies before providing any personal information.',
    ],
  },
  {
    icon: 'ri-refresh-line',
    title: 'Policy Updates',
    content: [
      'We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. The "Last Updated" date at the top of this page will indicate when changes were made.',
      'Continued use of our website after any changes constitutes your acceptance of the updated policy.',
    ],
  },
];

export default function PrivacyPolicyPage() {
  return (
    <main>
      <title>Privacy Policy | omandisposables.com — Wholesale Gloves Oman</title>
      <meta name="description" content="Privacy Policy for omandisposables.com. Learn how Oman's trusted wholesale glove supplier collects, uses, and protects your personal data." />
      <meta name="keywords" content="privacy policy, omandisposables, wholesale gloves Oman, data protection" />
      <link rel="canonical" href={`${SITE_URL}/privacy-policy`} />
      <meta property="og:title" content="Privacy Policy | omandisposables.com" />
      <meta property="og:description" content="How omandisposables.com collects, uses and protects your data. Wholesale gloves supplier based in Muscat, Oman." />
      <meta property="og:url" content={`${SITE_URL}/privacy-policy`} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://storage.readdy-site.link/project_files/21c16d12-4c00-4be0-9741-73f44fdf52b5/5879ee7c-c50e-493d-a57f-eb8051f7cc76_Blue-Bold-Hand-By-Hand-Care-Medical-Clinic-Logo.png?v=1b36c5545923081c64f3dfaaeeb02ab4" />
      <meta property="og:site_name" content="omandisposables.com" />
      <meta property="og:locale" content="en_US" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content="Privacy Policy | omandisposables.com" />
      <meta name="twitter:description" content="Privacy Policy for omandisposables.com — wholesale disposable gloves supplier in Oman." />
      <meta name="twitter:image" content="https://storage.readdy-site.link/project_files/21c16d12-4c00-4be0-9741-73f44fdf52b5/5879ee7c-c50e-493d-a57f-eb8051f7cc76_Blue-Bold-Hand-By-Hand-Care-Medical-Clinic-Logo.png?v=1b36c5545923081c64f3dfaaeeb02ab4" />
      <meta name="twitter:site" content="@omandisposables" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(PRIVACY_SCHEMA) }}
      />
      <Navbar />

      {/* Hero */}
      <section className="bg-navy pt-32 pb-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-14 h-14 flex items-center justify-center mx-auto mb-5 rounded-full bg-brand-green/20">
            <i className="ri-shield-check-line text-2xl text-brand-green" />
          </div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-white mb-3">
            Privacy Policy
          </h1>
          <p className="text-white/60 text-sm">
            Last Updated: <strong className="text-white/80">31 March 2026</strong>
          </p>
          <p className="text-white/70 text-sm mt-4 max-w-xl mx-auto leading-relaxed">
            Your privacy matters to us. This policy explains how{' '}
            <strong className="text-white">omandisposables.com</strong> collects, uses, and
            protects the information you share with us.
          </p>
        </div>
      </section>

      {/* Intro Banner */}
      <div className="bg-brand-green/10 border-y border-brand-green/20 py-4 px-4">
        <p className="max-w-3xl mx-auto text-center text-sm text-navy/80">
          <strong>omandisposables.com</strong> is based in Muscat, Oman. We are committed to
          protecting your personal data and complying with applicable data protection laws.
        </p>
      </div>

      {/* Legal Entity Disclosure */}
      <div className="bg-gray-50 py-3 px-4">
        <p className="max-w-3xl mx-auto text-center text-xs text-navy/50">
          omandisposables.com operates under Attiyaf Al Jazeera Household Utensils LLC, registered in Oman.
        </p>
      </div>

      {/* Content Sections */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto space-y-10">
          {SECTIONS.map((section) => (
            <div key={section.title} className="bg-gray-50 rounded-xl p-6 md:p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-navy/10">
                  <i className={`${section.icon} text-lg text-navy`} />
                </div>
                <h4 className="font-heading font-bold text-navy text-lg">
                  <a
                    id={section.title.toLowerCase().replace(/\s+/g, '-')}
                    href={`#${section.title.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {section.title}
                  </a>
                </h4>
              </div>
              <ul className="space-y-3">
                {section.content.map((point, idx) => (
                  <li key={idx} className="flex gap-3 text-sm text-navy/75 leading-relaxed">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-green flex-shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact Box */}
          <div className="bg-navy rounded-xl p-6 md:p-8 text-center">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 rounded-full bg-brand-green/20">
              <i className="ri-mail-line text-xl text-brand-green" />
            </div>
            <h4 className="font-heading font-bold text-white text-lg mb-2">
              Questions or Data Requests?
            </h4>
            <p className="text-white/70 text-sm mb-4 leading-relaxed">
              If you have any questions about this Privacy Policy, wish to access your data, or
              request deletion, please contact us at:
            </p>
            <a
              href="mailto:omandisposables@gmail.com"
              className="inline-flex items-center gap-2 bg-brand-green text-white font-heading font-semibold px-6 py-3 rounded-full hover:bg-brand-green/90 transition-all cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-mail-send-line" />
              omandisposables@gmail.com
            </a>
            <p className="text-white/40 text-xs mt-4">
              <i className="ri-map-pin-line mr-1" />
              omandisposables.com · Muscat, Oman
            </p>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}
